import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2, Paperclip, Phone, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionTag, scrollToId } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const inputCls =
  "w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

export function Field({ label, required, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-[11px] tracking-[0.2em] uppercase text-white/45 mb-2">
        {label}
        {required && <span className="text-gold"> *</span>}
      </span>
      {children}
    </label>
  );
}

export function presetProduct(product) {
  presetRequest({ product });
}

export function presetRequest({ product, type, note } = {}) {
  window.dispatchEvent(
    new CustomEvent("ajemi:preset-product", { detail: { product, type, note } })
  );
  scrollToId("quote");
}

const PRODUCTS = [
  "Jet A-1",
  "AGO / Diesel",
  "PMS",
  "Marine Fuels",
  "Fuel Oil",
  "Lubricants",
  "Other Petroleum Product",
];
const UNITS = ["Litres", "Metric Tonnes", "Barrels", "US Gallons"];
const FREQUENCIES = ["Weekly", "Bi-weekly", "Monthly", "Quarterly", "Custom"];

const INITIAL = {
  request_type: "one_time",
  product: "",
  quantity: "",
  unit: "Litres",
  delivery_location: "",
  required_date: "",
  start_date: "",
  frequency: "Monthly",
  num_locations: "",
  expected_duration: "",
  commercial_arrangement: "",
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  payment_terms: "",
  notes: "",
};

export function FilePicker({ files, setFiles, testId }) {
  const fileRef = useRef(null);
  const addFiles = (list) => {
    setFiles((prev) => [...prev, ...Array.from(list)].slice(0, 5));
  };
  return (
    <div data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-button`}
        onClick={() => fileRef.current?.click()}
        className="w-full border border-dashed border-white/15 rounded-lg px-4 py-5 text-sm text-white/45 hover:border-gold/50 hover:text-white/70 transition-colors duration-300 flex items-center justify-center gap-2"
      >
        <Paperclip className="w-4 h-4" />
        Attach LPO / PO / RFQ / contract / specifications (PDF, DOC, JPG, PNG — max 5)
      </button>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center justify-between text-sm text-white/60 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2"
            >
              <span className="truncate max-w-[85%]">{f.name}</span>
              <button
                type="button"
                data-testid={`${testId}-remove-${i}`}
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                className="text-white/40 hover:text-red-400 transition-colors"
                aria-label={`Remove ${f.name}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function QuoteSection() {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referenceId, setReferenceId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const d = e.detail;
      setForm((f) => {
        if (typeof d === "string") return { ...f, product: d };
        return {
          ...f,
          product: d?.product ?? f.product,
          request_type: d?.type ?? f.request_type,
          notes: d?.note ? (f.notes ? `${f.notes}\n${d.note}` : d.note) : f.notes,
        };
      });
    };
    window.addEventListener("ajemi:preset-product", handler);
    return () => window.removeEventListener("ajemi:preset-product", handler);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const recurring = form.request_type === "recurring";

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/quote-requests`, fd);
      setReferenceId(res.data.reference_id);
      scrollToId("quote");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed", {
        description: typeof detail === "string" ? detail : "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(217,164,65,0.06),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <SectionTag number="08" label="Request a Quote" />
              <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white">
                Tell us <span className="text-gold">what you need.</span>
              </h2>
              <p className="mt-6 text-white/55 text-base md:text-lg leading-relaxed max-w-md">
                One shipment, recurring supply or procurement across multiple locations —
                submit your requirement and the Ajemi team will contact you regarding
                available supply options.
              </p>
              <div className="mt-10 space-y-5">
                {[
                  "We review your product, volume and delivery requirements.",
                  "We identify suitable supply options through our network.",
                  "We contact you to discuss the available commercial options.",
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="font-outfit text-gold text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-white/60 leading-relaxed">{t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-col gap-3 text-sm">
                <a
                  data-testid="quote-phone-link"
                  href="tel:+2349151981008"
                  className="inline-flex items-center gap-3 text-white/60 hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4 text-gold" /> +234 915 198 1008
                </a>
                <a
                  data-testid="quote-phone-link-2"
                  href="tel:+2348060752250"
                  className="inline-flex items-center gap-3 text-white/60 hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4 text-gold" /> +234 806 075 2250
                </a>
                <a
                  data-testid="quote-email-link"
                  href="mailto:procurement@ajemienergy.com"
                  className="inline-flex items-center gap-3 text-white/60 hover:text-gold transition-colors"
                >
                  <Mail className="w-4 h-4 text-gold" /> procurement@ajemienergy.com
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="glass-panel rounded-2xl p-7 md:p-10" data-testid="quote-form-panel">
              {referenceId ? (
                <div className="text-center py-14" data-testid="quote-success">
                  <CheckCircle2 className="w-14 h-14 text-gold mx-auto" strokeWidth={1.2} />
                  <h3 className="font-outfit text-3xl font-light text-white mt-6">Request received.</h3>
                  <p className="text-white/60 mt-4 max-w-md mx-auto leading-relaxed">
                    Your procurement request has been submitted to Ajemi. A member of our team
                    will review your requirements and contact you regarding available supply
                    options.
                  </p>
                  <p className="mt-6 inline-block border border-gold/40 text-gold font-outfit tracking-[0.2em] text-sm rounded-full px-6 py-2.5" data-testid="quote-reference-id">
                    REF · {referenceId}
                  </p>
                  <div className="mt-10">
                    <button
                      data-testid="quote-submit-another-btn"
                      onClick={() => {
                        setReferenceId(null);
                        setForm(INITIAL);
                        setFiles([]);
                      }}
                      className="text-sm text-white/50 hover:text-gold transition-colors underline underline-offset-4"
                    >
                      Submit another request
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <span className="block text-[11px] tracking-[0.2em] uppercase text-white/45 mb-3">
                      What kind of requirement is this?
                    </span>
                    <div className="grid grid-cols-2 gap-3" data-testid="quote-type-toggle">
                      {[
                        { value: "one_time", label: "One-Time Procurement", hint: "A single shipment or delivery" },
                        { value: "recurring", label: "Recurring Supply", hint: "Ongoing / managed procurement" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`quote-type-${opt.value.replace("_", "-")}`}
                          onClick={() => setForm((f) => ({ ...f, request_type: opt.value }))}
                          className={`text-left rounded-xl border px-5 py-4 transition-all duration-300 ${
                            form.request_type === opt.value
                              ? "border-gold/60 bg-gold/10 shadow-[0_0_24px_rgba(217,164,65,0.15)]"
                              : "border-white/10 bg-white/[0.02] hover:border-white/25"
                          }`}
                        >
                          <span className={`block font-outfit text-sm ${form.request_type === opt.value ? "text-gold" : "text-white/80"}`}>
                            {opt.label}
                          </span>
                          <span className="block text-xs text-white/40 mt-1">{opt.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Field label="Product" required testId="quote-field-product">
                    <select
                      required
                      value={form.product}
                      onChange={set("product")}
                      className={inputCls}
                      data-testid="quote-product-select"
                    >
                      <option value="" disabled>
                        Select a product
                      </option>
                      {PRODUCTS.map((p) => (
                        <option key={p} value={p} className="bg-surface">
                          {p}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={recurring ? "Est. Monthly Volume" : "Quantity"} required testId="quote-field-quantity">
                      <input
                        required
                        type="number"
                        min="1"
                        value={form.quantity}
                        onChange={set("quantity")}
                        placeholder="e.g. 50,000"
                        className={inputCls}
                        data-testid="quote-quantity-input"
                      />
                    </Field>
                    <Field label="Unit" testId="quote-field-unit">
                      <select value={form.unit} onChange={set("unit")} className={inputCls} data-testid="quote-unit-select">
                        {UNITS.map((u) => (
                          <option key={u} value={u} className="bg-surface">
                            {u}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  {recurring ? (
                    <>
                      <Field label="Frequency" testId="quote-field-frequency">
                        <select value={form.frequency} onChange={set("frequency")} className={inputCls} data-testid="quote-frequency-select">
                          {FREQUENCIES.map((f) => (
                            <option key={f} value={f} className="bg-surface">
                              {f}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Number of Locations" testId="quote-field-num-locations">
                        <input
                          type="number"
                          min="1"
                          value={form.num_locations}
                          onChange={set("num_locations")}
                          placeholder="e.g. 3"
                          className={inputCls}
                          data-testid="quote-num-locations-input"
                        />
                      </Field>
                      <Field label="Delivery Location(s)" required testId="quote-field-location">
                        <input
                          required
                          value={form.delivery_location}
                          onChange={set("delivery_location")}
                          placeholder="Cities, depots, airports or ports"
                          className={inputCls}
                          data-testid="quote-location-input"
                        />
                      </Field>
                      <Field label="Start Date" testId="quote-field-start-date">
                        <input
                          type="date"
                          value={form.start_date}
                          onChange={set("start_date")}
                          className={inputCls}
                          data-testid="quote-start-date-input"
                        />
                      </Field>
                      <Field label="Expected Duration" testId="quote-field-duration">
                        <input
                          value={form.expected_duration}
                          onChange={set("expected_duration")}
                          placeholder="e.g. 12 months"
                          className={inputCls}
                          data-testid="quote-duration-input"
                        />
                      </Field>
                      <Field label="Preferred Commercial Arrangement" testId="quote-field-arrangement">
                        <input
                          value={form.commercial_arrangement}
                          onChange={set("commercial_arrangement")}
                          placeholder="e.g. monthly contract, per-delivery"
                          className={inputCls}
                          data-testid="quote-arrangement-input"
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Delivery Location" required testId="quote-field-location">
                        <input
                          required
                          value={form.delivery_location}
                          onChange={set("delivery_location")}
                          placeholder="City, depot, airport or port"
                          className={inputCls}
                          data-testid="quote-location-input"
                        />
                      </Field>
                      <Field label="Required Delivery Date" testId="quote-field-date">
                        <input
                          type="date"
                          value={form.required_date}
                          onChange={set("required_date")}
                          className={inputCls}
                          data-testid="quote-date-input"
                        />
                      </Field>
                    </>
                  )}

                  <Field label="Company Name" required testId="quote-field-company">
                    <input
                      required
                      value={form.company_name}
                      onChange={set("company_name")}
                      placeholder="Your company"
                      className={inputCls}
                      data-testid="quote-company-input"
                    />
                  </Field>
                  <Field label="Contact Name" required testId="quote-field-contact">
                    <input
                      required
                      value={form.contact_name}
                      onChange={set("contact_name")}
                      placeholder="Full name"
                      className={inputCls}
                      data-testid="quote-contact-input"
                    />
                  </Field>
                  <Field label="Email" required testId="quote-field-email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="name@company.com"
                      className={inputCls}
                      data-testid="quote-email-input"
                    />
                  </Field>
                  <Field label="Phone" testId="quote-field-phone">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+234 ..."
                      className={inputCls}
                      data-testid="quote-phone-input"
                    />
                  </Field>
                  {!recurring && (
                    <div className="sm:col-span-2">
                      <Field label="Preferred Payment Terms (optional)" testId="quote-field-terms">
                        <input
                          value={form.payment_terms}
                          onChange={set("payment_terms")}
                          placeholder="e.g. LC, advance, on delivery — per commercial agreement"
                          className={inputCls}
                          data-testid="quote-terms-input"
                        />
                      </Field>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Field label="Additional Requirements" testId="quote-field-notes">
                      <textarea
                        rows={4}
                        value={form.notes}
                        onChange={set("notes")}
                        placeholder="Specifications, volumes split across locations, timelines, anything else we should know."
                        className={`${inputCls} resize-none`}
                        data-testid="quote-notes-input"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <FilePicker files={files} setFiles={setFiles} testId="quote-file-upload" />
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="quote-submit-btn"
                      className="group w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full px-8 py-4 text-base hover:bg-goldbright hover:shadow-[0_0_44px_rgba(217,164,65,0.5)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          Submit Procurement Request
                          <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
                        </>
                      )}
                    </button>
                    <p className="text-center text-[11px] text-white/35 mt-4">
                      Payment and settlement follow the agreed commercial arrangement between
                      the transaction parties and applicable requirements. Ajemi does not hold
                      customer funds.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
